import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, InputAdornment, CircularProgress, Alert, IconButton, Paper, Button, Snackbar,
  Collapse, Chip, Dialog, DialogTitle, DialogContent, Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import EditLocationAltIcon from '@mui/icons-material/EditLocationAlt';
import DoneIcon from '@mui/icons-material/Done';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import ReCAPTCHA from 'react-google-recaptcha';
import PageLayout from '../components/common/PageLayout';
import { get, patch, post } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

// Only this account may use Manage (drag-to-place + Rebuild); everyone else gets a read-only locator.
const MANAGE_EMAIL = 'tprabha@gmail.com';

const IMG = `${process.env.PUBLIC_URL || ''}/assets/images/srichakra-yantra.jpg`;
const LINGAM = `${process.env.PUBLIC_URL || ''}/assets/images/shivalingam-marker.png`;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const RECAPTCHA_ENABLED = RECAPTCHA_SITE_KEY && RECAPTCHA_SITE_KEY.trim() !== '';

// Levels key (bindu -> outward). Colours are the element's actual colour on the yantra. Counts and
// tiers are derived live from the loaded slots; this array carries the fixed labels only.
const LEGEND = [
  { code: 'A', color: '#c9962b', el: 'bindu', name: 'Sarvanandamaya', lingas: 'Siva & Parvati Murthis (Bronze Statues)', tier: '250k' },
  { code: 'B', color: '#e23b3b', el: 'red triangle', name: 'Sarva Siddhiprada', lingas: 'Sadasiva · Chandiswara · Chandramouliswara' },
  { code: 'C', color: '#2b6bd6', el: 'blue triangles', name: 'Sarva Rogahara', lingas: 'Sapta Matrika & Maha Lakshmi' },
  { code: 'D', color: '#f2c200', el: 'yellow triangles', name: 'Sarva Rakshakara', lingas: 'Jyotirlingas (10 of 12)' },
  { code: 'E', color: '#d63bd6', el: 'magenta triangles', name: 'Sarvardha Sadhaka', lingas: '2 Jyotirlingas · Dakshinamurthi · 7 Ashta Murthis' },
  { code: 'F', color: '#35c04a', el: 'green triangles', name: 'Sarva Sowbhagyadayaka', lingas: '5 Pancharamas · 5 Panchabhutas · 1 Ashta Murthi · Markandeya · 2 Saptarishis' },
  { code: 'G', color: '#f2c84b', el: 'inner 8-petal lotus', name: 'Sarva Sankshobhana', lingas: '5 Saptarishis · Ganga · Parvati · Ganapati' },
  { code: 'H', color: '#c23bb0', el: 'outer 16-petal lotus', name: 'Sarvasa Paripooraka', lingas: '11 Ekadasa Rudras · Gauri · 4 Vedas' },
  { code: 'I', color: '#2b6bd6', el: 'inner circle', name: 'Samhara', lingas: 'Ashta Bhetalas' },
  { code: 'J', color: '#35c04a', el: 'middle circle', name: 'Sthiti', lingas: '3 Pancha Palakas · 9 Navagrahas' },
  { code: 'K', color: '#e23b3b', el: 'outer circle', name: 'Srishti', lingas: 'Trisula Lingas' },
  { code: 'L', color: '#b9aede', el: 'inner square', name: 'Trailokya Mohana', lingas: '8 Dikpalakas · 4 Adityas · 8 Dwarapalakas' },
  { code: 'M', color: '#f2c200', el: 'middle square', name: 'Trailokya Mohana', lingas: '8 Dikpalakas · 4 Adityas · 8 Dwarapalakas' },
  { code: 'N', color: '#35c04a', el: 'outer square', name: 'Trailokya Mohana', lingas: '8 Dikpalakas · 4 Adityas · 8 Dwarapalakas · 8 Jiva Nadis' },
];
// Zoom factor per level so dense inner rings spread out; outer rings/squares stay ~1x.
const ZOOM = { B: 3.4, C: 2.7, D: 2.5, E: 2.5, F: 2.3, G: 2.0, H: 1.8, I: 1.7, J: 1.55, K: 1.45 };
// Bindu (centre) — the A level has no linga slot; this marks the Murthis at the yantra's centre.
const BINDU = { x: 0.5, y: 0.4986 };
const clamp = (v) => Math.min(1, Math.max(0, v));

// Sri Chakram Linga Locator. One component, three modes:
//  - admin      : staff login (this route is inside PrivateRoute); Manage/Rebuild + sponsors.
//  - restricted : open link, gated by an access code + reCAPTCHA; read-only, shows sponsors.
//  - public     : open link, no gate; read-only, NO sponsor names, search by linga name only.
export default function SriChakramLocatorPage({ mode = 'admin' }) {
  const navigate = useNavigate();
  const { staff } = useAuth();
  const isAdmin = mode === 'admin';
  const isPublic = mode === 'public';
  const isRestricted = mode === 'restricted';
  const showSponsors = !isPublic;      // admin + restricted show sponsors; public hides them
  const canManage = isAdmin && String(staff?.email || '').trim().toLowerCase() === MANAGE_EMAIL;

  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [q, setQ] = React.useState('');
  const [pinned, setPinned] = React.useState(null);
  const [manage, setManage] = React.useState(false);
  const [drag, setDrag] = React.useState(null);      // code being dragged
  const [toast, setToast] = React.useState('');
  const [rebuilding, setRebuilding] = React.useState(false);
  const [hover, setHover] = React.useState(null);    // code hovered (view mode) -> popup
  const [hoverLabel, setHoverLabel] = React.useState(null); // callout label hovered -> raise to front + expand
  const [legendOpen, setLegendOpen] = React.useState(true);
  const [activeLevel, setActiveLevel] = React.useState(null); // legend row -> highlight that level's dots
  const [detailLevel, setDetailLevel] = React.useState(null); // legend row Details icon -> dialog
  // restricted gate
  const [unlocked, setUnlocked] = React.useState(!isRestricted);
  const [code, setCode] = React.useState('');
  const [captcha, setCaptcha] = React.useState(null);
  const recaptchaRef = React.useRef(null);
  const imgRef = React.useRef(null);

  const load = React.useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await get(isPublic ? '/api/public/srichakram/locations' : '/ldb/kotilinga-sri-chakram/locations');
      setData(res?.data || []);
    } catch (e) { setError(e?.message || 'Failed to load the Sri Chakram locations'); }
    finally { setLoading(false); }
  }, [isPublic]);
  React.useEffect(() => { if (isRestricted) { setLoading(false); } else { load(); } }, [load, isRestricted]);

  const doUnlock = async (e) => {
    e.preventDefault();
    if (RECAPTCHA_ENABLED && !captcha) { setError('Please complete the reCAPTCHA verification.'); return; }
    setLoading(true); setError('');
    try {
      const res = await post('/api/public/srichakram/unlock', { code: code.trim(), recaptchaToken: captcha });
      setData(res?.data || []); setUnlocked(true);
    } catch (err) {
      setError(err?.message || 'Could not unlock — check the code and try again.');
      recaptchaRef.current?.reset?.(); setCaptcha(null);
    } finally { setLoading(false); }
  };

  const term = q.trim().toLowerCase();
  const hits = React.useMemo(() => (term.length >= 3 && !manage
    ? data.filter((s) => (isPublic ? `${s.suggested_linga || ''}` : `${s.suggested_linga || ''} ${s.sponsor || ''}`).toLowerCase().includes(term))
    : []), [term, data, manage, isPublic]);
  React.useEffect(() => { setPinned(null); }, [term]);
  // One search mode at a time: typing a query clears any chosen level.
  React.useEffect(() => { if (term.length >= 3) setActiveLevel(null); }, [term]);

  const focus = !manage && (hits.length === 1 ? hits[0] : (pinned ? data.find((s) => s.code === pinned) : null));
  const dimmed = Boolean(focus) || hits.length > 1 || Boolean(activeLevel);
  const popupSlot = focus || (!manage && hover ? data.find((s) => s.code === hover) : null);
  const pct = (v) => `${(Number(v) * 100).toFixed(3)}%`;

  // Zoom + scaled-fraction helpers (the image is CSS-scaled about its centre when a dense level is active;
  // callouts/popups live in a NON-scaled overlay and use the scaled fractional position).
  const zoom = (!manage && activeLevel && ZOOM[activeLevel]) || 1;
  const sf = (f) => 0.5 + (Number(f) - 0.5) * zoom;
  const pctS = (f) => `${(sf(f) * 100).toFixed(3)}%`;
  const calloutSlots = (!manage && activeLevel && activeLevel !== 'A')
    ? data.filter((s) => s.level_code === activeLevel)
    : (!manage && hits.length > 1 ? hits : []);

  const levelMeta = React.useMemo(() => {
    const m = {};
    data.forEach((s) => {
      const e = m[s.level_code] || { count: 0, avail: 0, prices: new Set() };
      e.count += 1; if (!s.sponsor) e.avail += 1; if (s.price) e.prices.add(Number(s.price));
      m[s.level_code] = e;
    });
    return m;
  }, [data]);
  // Level description = the actual lingas (key-name), in key order. Category labels are ambiguous because
  // some category names contain member names (Markandeya, Parvati, Ganga), so we list the real lingas.
  const levelLingas = React.useMemo(() => {
    const m = {};
    data.forEach((s) => { (m[s.level_code] = m[s.level_code] || []).push(s); });
    Object.values(m).forEach((arr) => arr.sort((a, b) => a.position_no - b.position_no));
    return m;
  }, [data]);
  const fmtAmt = (n) => { const v = Number(n); if (!v) return ''; const k = v / 1000; return `${Number.isInteger(k) ? k : k.toFixed(1)}k`; };
  // Shared info block used by the search-result cards and the hover/pinned popup. Sponsor hidden in public.
  const InfoLines = ({ s, big }) => (
    <>
      <Typography sx={{ fontWeight: 700, fontSize: big ? 15 : 14 }}>
        {s.suggested_linga || 'Open spot'}
        {fmtAmt(s.price) && <Box component="span" sx={{ color: 'success.main', fontWeight: 700 }}>{` · ${fmtAmt(s.price)}`}</Box>}
      </Typography>
      {s.category_name && <Typography sx={{ fontSize: 12, color: 'text.primary' }}>{s.category_name}</Typography>}
      <Typography sx={{ fontSize: 12, color: '#c1121f', fontWeight: 600 }}>{s.srichakram_name} Chakram · {s.code}</Typography>
      {showSponsors && <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{s.sponsor ? `Gifted by ${s.sponsor}` : 'Available to sponsor'}</Typography>}
    </>
  );

  const fromEvent = (e) => {
    const r = imgRef.current.getBoundingClientRect();
    return { x: clamp((e.clientX - r.left) / r.width), y: clamp((e.clientY - r.top) / r.height) };
  };
  const onDown = (e, s) => {
    if (!manage) return;
    e.preventDefault(); e.currentTarget.setPointerCapture(e.pointerId); setDrag(s.code);
  };
  const onMove = (e, s) => {
    if (drag !== s.code) return;
    const p = fromEvent(e);
    setData((d) => d.map((x) => (x.code === s.code ? { ...x, x_frac: p.x, y_frac: p.y } : x)));
  };
  const onUp = async (e, s) => {
    if (drag !== s.code) return;
    setDrag(null);
    const cur = data.find((x) => x.code === s.code);
    try {
      await patch(`/ldb/kotilinga-sri-chakram/slots/${s.code}`, { x_frac: cur.x_frac, y_frac: cur.y_frac });
      setToast(`${s.code} moved`);
    } catch (err) { setError(`Could not save ${s.code}: ${err?.message || err}`); load(); }
  };

  const doRebuild = async () => {
    if (!window.confirm('Rebuild the full Sri Chakram layout from the bundled map?\n\nThis overwrites all slot positions and re-assigns each linga its own sponsor.')) return;
    setRebuilding(true); setError('');
    try {
      const r = await post('/ldb/kotilinga-sri-chakram/rebuild', {});
      const un = (r?.unresolved || []).length;
      setToast(`Rebuilt ${r?.resolved || 0}/${r?.slots || 0} · saved ${r?.post_commit_rows ?? '?'}${un ? ` · ${un} unresolved` : ''}`);
      await load();
    } catch (err) { setError(`Rebuild failed: ${err?.message || err}`); }
    finally { setRebuilding(false); }
  };

  // Download the full placement as CSV (admin): key, chakram/level, lingam, sponsor, price.
  const downloadCsv = () => {
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const header = ['Key', 'Chakram / Level', 'Product Category', 'Lingam', 'Product ID', 'Sponsor', 'Price'];
    const lines = [header.map(esc).join(',')].concat(
      data.map((s) => [s.code, s.srichakram_name, s.category_name || '', s.suggested_linga || '', s.product_id || '', s.sponsor || 'Available', s.price ? Number(s.price) : ''].map(esc).join(','))
    );
    const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'sri-chakram-lingas.csv'; document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const selectLevel = (c) => { setActiveLevel((cur) => (cur === c ? null : c)); setQ(''); setPinned(null); };

  const hint = manage ? 'Drag any dot to reposition it — changes save automatically.'
    : (!term ? `Type a linga${showSponsors ? ' or a sponsor’s' : ''} name to locate it — or open a level below.`
      : (term.length < 3 ? 'Keep typing… (at least 3 letters)'
        : (hits.length === 0 ? 'No match — try another spelling.'
          : (hits.length === 1 ? `Found it — ${hits[0].code}` : `${hits.length} matches — labelled on the yantra${showSponsors ? ' and listed at right' : ''}.`))));

  const dirSx = { position: 'absolute', fontWeight: 800, color: '#c1121f', fontSize: { xs: 15, sm: 22 }, letterSpacing: 1, lineHeight: 1, pointerEvents: 'none', zIndex: 5 };

  const DotMarker = ({ s }) => {
    const isFocus = focus && s.code === focus.code;
    const isHit = hits.some((h) => h.code === s.code);
    const isDrag = drag === s.code;
    const levelActive = activeLevel && s.level_code === activeLevel;
    const pulsing = !manage && (isFocus || (hits.length > 1 && isHit) || levelActive);
    const dimOut = !manage && dimmed && !isFocus && !(hits.length > 1 && isHit) && !levelActive;
    return (
      <Box
        onPointerDown={(e) => onDown(e, s)}
        onPointerMove={(e) => onMove(e, s)}
        onPointerUp={(e) => onUp(e, s)}
        onClick={() => !manage && setPinned(s.code)}
        onMouseEnter={() => !manage && setHover(s.code)}
        onMouseLeave={() => !manage && setHover((h) => (h === s.code ? null : h))}
        sx={{
          position: 'absolute', left: pct(s.x_frac), top: pct(s.y_frac),
          transform: `translate(-50%,-50%) scale(${(manage ? 1.35 : 1) / zoom})`,
          opacity: dimOut ? 0.18 : 1,
          cursor: manage ? (isDrag ? 'grabbing' : 'grab') : 'pointer',
          zIndex: isDrag ? 30 : (pulsing ? 9 : 1),
          transition: isDrag ? 'none' : 'transform .13s ease, opacity .2s ease',
          touchAction: 'none',
        }}
      >
        <Box sx={{
          '@keyframes schkBeat': { '0%,100%': { transform: 'scale(1)' }, '50%': { transform: 'scale(1.32)' } },
          width: pulsing ? 22 : (s.shape === 'square' ? 11 : 9),
          height: pulsing ? 24 : (s.shape === 'square' ? 11 : 9),
          borderRadius: pulsing ? 0 : (s.shape === 'square' ? '2px' : '50%'),
          background: pulsing ? 'transparent' : (manage ? '#1565c0' : '#fff'),
          backgroundImage: pulsing ? `url(${LINGAM})` : 'none',
          backgroundSize: 'contain', backgroundRepeat: 'no-repeat', backgroundPosition: 'center',
          filter: pulsing ? 'drop-shadow(0 0 1px #fff) drop-shadow(0 0 2px #fff)' : 'none',
          border: pulsing ? 'none' : `1.4px solid ${manage ? '#fff' : '#3a3a3a'}`,
          boxShadow: pulsing ? 'none' : (isDrag ? '0 0 0 3px #1565c0, 0 4px 14px rgba(0,0,0,.6)' : '0 1px 2px rgba(0,0,0,.4)'),
          animation: pulsing ? 'schkBeat 1.1s ease-in-out infinite' : 'none',
        }} />
        {manage && (
          <Box component="span" sx={{ position: 'absolute', left: '130%', top: '50%', transform: 'translateY(-50%)', fontSize: 9, fontWeight: 700, color: '#0d3c78', whiteSpace: 'nowrap', textShadow: '0 0 2px #fff,0 0 2px #fff,0 0 2px #fff', pointerEvents: 'none' }}>
            {s.code}
          </Box>
        )}
      </Box>
    );
  };

  const popupCaret = { content: '""', position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', border: '7px solid transparent', borderTopColor: 'background.paper' };

  // Restricted gate — shown until the access code + reCAPTCHA are accepted.
  if (isRestricted && !unlocked) {
    return (
      <PageLayout contentMaxWidth="sm" showHeaderImage={false} showOrgHeader showAdminActions={false}>
        <Box component="form" onSubmit={doUnlock} sx={{ maxWidth: 420, mx: 'auto', mt: { xs: 3, sm: 6 }, textAlign: 'center' }}>
          <LockOutlinedIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Sri Chakram Linga Locator</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Enter your access code to continue.</Typography>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          <TextField fullWidth type="password" label="Access code" value={code} autoFocus
            onChange={(e) => setCode(e.target.value)} sx={{ mb: 2 }} />
          {RECAPTCHA_ENABLED && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <ReCAPTCHA ref={recaptchaRef} sitekey={RECAPTCHA_SITE_KEY} onChange={setCaptcha} />
            </Box>
          )}
          <Button type="submit" variant="contained" fullWidth disabled={loading || !code.trim()}>
            {loading ? 'Checking…' : 'Unlock'}
          </Button>
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout contentMaxWidth="lg" showHeaderImage={false} showOrgHeader showAdminActions={isAdmin}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        {isAdmin && (
          <IconButton onClick={() => navigate('/kotilinga/sponsorships')} size="small" aria-label="Back"><ArrowBackIcon /></IconButton>
        )}
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.15 }}>Sri Chakram Linga Locator</Typography>
          <Typography variant="body2" color="text.secondary">
            Search a linga{showSponsors ? ' or a sponsor' : ''} to show its exact place on the Sri Chakram.
          </Typography>
        </Box>
        {isAdmin && !manage && data.length > 0 && (
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadCsv} sx={{ mr: 1 }}>Download</Button>
        )}
        {canManage && manage && (
          <Button
            variant="outlined" color="warning" startIcon={<RefreshIcon />}
            disabled={rebuilding} onClick={doRebuild} sx={{ mr: 1 }}
          >
            {rebuilding ? 'Rebuilding…' : 'Rebuild layout'}
          </Button>
        )}
        {canManage && (
          <Button
            variant={manage ? 'contained' : 'outlined'} color={manage ? 'success' : 'primary'}
            startIcon={manage ? <DoneIcon /> : <EditLocationAltIcon />}
            onClick={() => { setManage((m) => !m); setQ(''); setPinned(null); setActiveLevel(null); }}
          >
            {manage ? 'Done' : 'Manage'}
          </Button>
        )}
      </Box>

      {!manage && (
        <TextField
          fullWidth value={q} onChange={(e) => setQ(e.target.value)}
          placeholder={`Search a linga${showSponsors ? ' or sponsor' : ''} — e.g. Somanatha`} autoComplete="off"
          sx={{ maxWidth: 560, mb: 0.5 }}
          InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
        />
      )}
      <Typography variant="caption" color={manage ? 'success.main' : 'text.secondary'} sx={{ display: 'block', mb: 2 }}>{hint}</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: hits.length > 1 ? '1fr 300px' : '1fr' }, gap: 2, alignItems: 'start' }}>
          <Box sx={{ position: 'relative', width: '100%', maxWidth: 1060, mx: 'auto', px: { xs: '46px', sm: '64px' }, py: { xs: '28px', sm: '38px' } }}>
            <Typography sx={{ ...dirSx, top: 0, left: '50%', transform: 'translateX(-50%)' }}>SOUTH</Typography>
            <Typography sx={{ ...dirSx, bottom: 0, left: '50%', transform: 'translateX(-50%)' }}>NORTH</Typography>
            <Typography sx={{ ...dirSx, left: 0, top: '50%', transform: 'translateY(-50%)' }}>EAST</Typography>
            <Typography sx={{ ...dirSx, right: 0, top: '50%', transform: 'translateY(-50%)' }}>WEST</Typography>

            <Box onMouseLeave={() => setHover(null)} sx={{ position: 'relative', width: '100%' }}>
              <Box sx={{ position: 'relative', width: '100%', borderRadius: 2, boxShadow: 3, overflow: zoom > 1 ? 'hidden' : 'visible', outline: manage ? '2px dashed' : 'none', outlineColor: 'success.main' }}>
                <Box
                  ref={imgRef}
                  sx={{ position: 'relative', width: '100%', transformOrigin: '50% 50%', transform: `scale(${zoom})`, transition: 'transform .3s ease', touchAction: manage ? 'none' : 'auto' }}
                >
                  <Box component="img" src={IMG} alt="Sri Chakram" draggable={false} sx={{ display: 'block', width: '100%', userSelect: 'none' }} />
                  <Box sx={{ position: 'absolute', inset: 0 }}>
                    {data.map((s) => <DotMarker key={s.code} s={s} />)}
                  </Box>
                </Box>
              </Box>

              <Box sx={{ position: 'absolute', inset: 0, overflow: 'visible', pointerEvents: 'none', zIndex: 6 }}>
                {calloutSlots.length > 0 && (() => {
                  const gap = 0.058;
                  const place = (side) => {
                    const arr = calloutSlots
                      .filter((s) => (side === 'L' ? sf(s.x_frac) < 0.5 : sf(s.x_frac) >= 0.5))
                      .sort((a, b) => sf(a.y_frac) - sf(b.y_frac));
                    let y = -Infinity;
                    const out = arr.map((s) => { y = Math.max(sf(s.y_frac), y + gap); return { s, y }; });
                    if (out.length && out[out.length - 1].y > 1) { const sh = out[out.length - 1].y - 1; out.forEach((o) => { o.y -= sh; }); }
                    if (out.length && out[0].y < 0) { const sh = -out[0].y; out.forEach((o) => { o.y += sh; }); }
                    return out;
                  };
                  const all = [
                    ...place('L').map((o) => ({ ...o, lx: -0.008, side: 'L' })),
                    ...place('R').map((o) => ({ ...o, lx: 1.008, side: 'R' })),
                  ];
                  return (
                    <>
                      <Box component="svg" viewBox="0 0 100 100" preserveAspectRatio="none"
                        sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible' }}>
                        {all.map(({ s, y, lx }) => (
                          <line key={s.code} x1={sf(s.x_frac) * 100} y1={sf(s.y_frac) * 100} x2={lx * 100} y2={y * 100}
                            stroke="#c1121f" strokeWidth="0.22" opacity="0.7" />
                        ))}
                      </Box>
                      {all.map(({ s, y, lx, side }) => {
                        const hl = hoverLabel === s.code;
                        return (
                        <Box key={s.code}
                          onMouseEnter={() => setHoverLabel(s.code)}
                          onMouseLeave={() => setHoverLabel((h) => (h === s.code ? null : h))}
                          sx={{
                            position: 'absolute', top: `${y * 100}%`, left: `${lx * 100}%`,
                            transform: `translate(${side === 'L' ? '-100%' : '0'}, -50%)`,
                            width: hl ? 'max-content' : { xs: 132, sm: 158 }, maxWidth: hl ? 300 : undefined,
                            pointerEvents: 'auto', textAlign: side === 'L' ? 'right' : 'left',
                            zIndex: hl ? 40 : 7,
                          }}>
                          <Box sx={{ display: 'inline-block', bgcolor: hl ? '#fff' : 'rgba(255,255,255,.96)', border: '1px solid', borderColor: hl ? 'primary.main' : 'divider', borderRadius: 1, px: 0.75, py: 0.35, boxShadow: hl ? 6 : 1, maxWidth: '100%' }}>
                            <Typography noWrap={!hl} sx={{ fontSize: 13.5, fontWeight: 700, lineHeight: 1.3 }}>
                              <Box component="span" sx={{ color: 'warning.main' }}>{s.code}</Box>{` ${s.suggested_linga || ''}`}
                              {fmtAmt(s.price) && <Box component="span" sx={{ color: 'success.main' }}>{` · ${fmtAmt(s.price)}`}</Box>}
                            </Typography>
                            {s.category_name && <Typography noWrap={!hl} sx={{ fontSize: 11.5, color: 'text.primary', lineHeight: 1.3 }}>{s.category_name}</Typography>}
                            <Typography noWrap={!hl} sx={{ fontSize: 11.5, color: '#c1121f', fontWeight: 600, lineHeight: 1.3 }}>{s.srichakram_name} Chakram</Typography>
                            {showSponsors && (
                              <Typography noWrap={!hl} sx={{ fontSize: 13, fontWeight: 600, color: s.sponsor ? 'text.secondary' : 'success.main', lineHeight: 1.3 }}>
                                {s.sponsor || 'Available'}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        );
                      })}
                    </>
                  );
                })()}

                {activeLevel === 'A' && (
                  <>
                    <Box sx={{
                      position: 'absolute', left: pctS(BINDU.x), top: pctS(BINDU.y), width: 22, height: 24,
                      backgroundImage: `url(${LINGAM})`, backgroundSize: 'contain', backgroundRepeat: 'no-repeat', backgroundPosition: 'center',
                      filter: 'drop-shadow(0 0 1px #fff) drop-shadow(0 0 2px #fff)', zIndex: 12,
                      '@keyframes schkBeatC': { '0%,100%': { transform: 'translate(-50%,-50%) scale(1)' }, '50%': { transform: 'translate(-50%,-50%) scale(1.32)' } },
                      transform: 'translate(-50%,-50%)', animation: 'schkBeatC 1.1s ease-in-out infinite',
                    }} />
                    <Paper elevation={6} sx={{
                      position: 'absolute', left: pctS(BINDU.x), top: pctS(BINDU.y),
                      transform: 'translate(-50%,-100%)', mt: '-16px', zIndex: 20, px: 1.75, py: 1.25,
                      textAlign: 'center', whiteSpace: 'nowrap', borderRadius: 2, '&::after': popupCaret,
                    }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 15 }}>Siva &amp; Parvati Murthis<Box component="span" sx={{ color: 'success.main', fontWeight: 700 }}>{' · 250k'}</Box></Typography>
                      <Typography sx={{ fontSize: 12, color: '#c1121f', fontWeight: 600 }}>Sarvanandamaya (bindu) · A</Typography>
                      <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>Bronze Statues</Typography>
                    </Paper>
                  </>
                )}

                {popupSlot && activeLevel !== 'A' && (
                  <Paper elevation={6} sx={{
                    position: 'absolute', left: pctS(popupSlot.x_frac), top: pctS(popupSlot.y_frac),
                    transform: 'translate(-50%,-100%)', mt: '-16px', zIndex: 20, px: 1.75, py: 1.25,
                    textAlign: 'center', whiteSpace: 'nowrap', borderRadius: 2, '&::after': popupCaret,
                  }}>
                    <InfoLines s={popupSlot} big />
                  </Paper>
                )}
              </Box>
            </Box>
          </Box>

          {hits.length > 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, maxHeight: 560, overflow: 'auto' }}>
              {hits.slice(0, 80).map((s) => (
                <Paper key={s.code} variant="outlined" onClick={() => setPinned(s.code)}
                  onMouseEnter={() => setHover(s.code)} onMouseLeave={() => setHover((h) => (h === s.code ? null : h))}
                  sx={{ p: 1.25, cursor: 'pointer', borderColor: pinned === s.code ? 'primary.main' : 'divider', '&:hover': { borderColor: 'primary.main' } }}>
                  <InfoLines s={s} />
                </Paper>
              ))}
            </Box>
          )}
        </Box>
      )}
      {!loading && !manage && (
        <Box sx={{ mt: 2.5, maxWidth: 1060, mx: 'auto' }}>
          <Button
            onClick={() => setLegendOpen((o) => !o)}
            startIcon={<KeyboardArrowDownIcon sx={{ transform: legendOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Srichakra Levels{data.length ? ` — ${data.length} lingas, bindu → outward` : ''}
          </Button>
          <Collapse in={legendOpen}>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
              {LEGEND.map((lv) => {
                const meta = levelMeta[lv.code] || { count: 0, prices: new Set() };
                const isA = lv.code === 'A';
                const tiers = isA ? (lv.tier || '') : [...meta.prices].sort((a, b) => b - a).map(fmtAmt).join(' / ');
                const on = activeLevel === lv.code;
                const swatchRadius = lv.el.includes('square') ? '3px' : (lv.el.includes('triangle')) ? '2px' : '50%';
                return (
                  <Box
                    key={lv.code}
                    onClick={() => selectLevel(lv.code)}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '18px 24px 1fr 44px 32px', sm: '22px 30px 176px 1fr 66px 28px 96px 34px' },
                      alignItems: 'center', gap: 1, px: 1.25, py: 0.9,
                      borderTop: isA ? 'none' : '1px solid', borderColor: 'divider',
                      bgcolor: on ? 'action.selected' : 'transparent',
                      cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Box sx={{ width: 16, height: 16, borderRadius: swatchRadius, bgcolor: lv.color, border: '1px solid rgba(0,0,0,.28)' }} />
                    <Chip label={lv.code} size="small" sx={{ height: 20, fontWeight: 700, '& .MuiChip-label': { px: 0.75 } }} />
                    <Box sx={{ display: { xs: 'none', sm: 'block' }, minWidth: 0 }}>
                      <Typography noWrap sx={{ fontWeight: 700, fontSize: 13, lineHeight: 1.15 }}>{lv.name}</Typography>
                      <Typography noWrap sx={{ fontSize: 10.5, color: 'text.secondary' }}>{lv.el}</Typography>
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Box component="span" sx={{ display: { sm: 'none' }, fontWeight: 700, fontSize: 12.5 }}>{lv.name}</Box>
                      {(levelLingas[lv.code] || []).length
                        ? (() => {
                          // group the level's lingas by category (first-seen order) -> bold header + count, then key-names
                          const groups = []; const gi = {};
                          for (const s of levelLingas[lv.code]) {
                            const c = s.category_name || '—';
                            if (!(c in gi)) { gi[c] = groups.length; groups.push({ cat: c, items: [] }); }
                            groups[gi[c]].items.push(s);
                          }
                          return groups.map((g) => (
                            <Box key={g.cat} sx={{ mt: 0.35 }}>
                              <Typography component="div" sx={{ fontSize: 11.5, lineHeight: 1.3 }}>
                                <Box component="span" sx={{ fontWeight: 700 }}>{g.cat}</Box>
                                <Box component="span" sx={{ color: 'text.secondary' }}>{` (${g.items.length})`}</Box>
                              </Typography>
                              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0,1fr))', sm: 'repeat(3, minmax(0,1fr))', md: 'repeat(4, minmax(0,1fr))' }, columnGap: 1.5, rowGap: 0.1 }}>
                                {g.items.map((s) => {
                                  const avail = showSponsors && !s.sponsor;
                                  return (
                                    <Box component="span" key={s.code} title={`${s.code}-${s.suggested_linga || ''}`}
                                      sx={{ fontSize: 12, lineHeight: 1.4, color: avail ? 'success.main' : 'text.primary', fontWeight: avail ? 700 : 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {avail ? '*' : ''}{s.code}-{s.suggested_linga || '—'}
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Box>
                          ));
                        })()
                        : <Box component="span" sx={{ fontSize: 12.5 }}>{lv.lingas}</Box>}
                    </Box>
                    <Typography sx={{ display: { xs: 'none', sm: 'block' }, fontSize: 12, color: 'success.main', fontWeight: 700, textAlign: 'right' }}>{tiers}</Typography>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary', textAlign: 'right' }}>{isA ? '—' : meta.count}</Typography>
                    <Typography sx={{ display: { xs: 'none', sm: 'block' }, fontSize: 11.5, fontWeight: 700, color: 'success.main', textAlign: 'right' }}>
                      {!isA && meta.avail > 0 ? `${meta.avail} available` : ''}
                    </Typography>
                    <IconButton size="small" aria-label={`Details for ${lv.name}`}
                      onClick={(e) => { e.stopPropagation(); setDetailLevel(lv); }}
                      sx={{ justifySelf: 'end' }}>
                      <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>
            {activeLevel && (
              <Typography sx={{ mt: 1, fontSize: 12.5, color: 'text.secondary' }}>
                Showing level <b>{activeLevel}</b> on the yantra{zoom > 1 ? ' (zoomed in)' : ''} —{' '}
                <Box component="span" onClick={() => setActiveLevel(null)} sx={{ cursor: 'pointer', color: 'primary.main', fontWeight: 600 }}>clear</Box>
              </Typography>
            )}
          </Collapse>
        </Box>
      )}
      {detailLevel && (() => {
        const isA = detailLevel.code === 'A';
        const rows = isA
          ? [{ code: 'A', suggested_linga: 'Siva & Parvati Murthis', sponsor: null, price: 250000 }]
          : data.filter((s) => s.level_code === detailLevel.code).sort((a, b) => a.position_no - b.position_no);
        const pos = LEGEND.length - LEGEND.findIndex((l) => l.code === detailLevel.code);
        return (
          <Dialog open onClose={() => setDetailLevel(null)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 16, height: 16, borderRadius: '3px', bgcolor: detailLevel.color, border: '1px solid rgba(0,0,0,.28)' }} />
                <Typography component="span" sx={{ fontWeight: 800, fontSize: 18 }}>{detailLevel.name}</Typography>
                <Chip label={detailLevel.code} size="small" sx={{ fontWeight: 700 }} />
              </Box>
              <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.5 }}>
                {detailLevel.el} · Level {pos} of {LEGEND.length} from the base · {isA ? 'Bronze statues (not lingas)' : `${rows.length} lingas`}
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ display: 'grid', gridTemplateColumns: showSponsors ? '58px 1fr 1fr' : '58px 1fr', columnGap: 1, rowGap: 0.6, alignItems: 'baseline' }}>
                <Typography sx={{ fontWeight: 700, fontSize: 12, color: 'text.secondary' }}>Key</Typography>
                <Typography sx={{ fontWeight: 700, fontSize: 12, color: 'text.secondary' }}>Lingam</Typography>
                {showSponsors && <Typography sx={{ fontWeight: 700, fontSize: 12, color: 'text.secondary' }}>Sponsor</Typography>}
                <Box sx={{ gridColumn: '1 / -1', my: 0.3 }}><Divider /></Box>
                {rows.map((r) => (
                  <React.Fragment key={r.code}>
                    <Typography sx={{ fontWeight: 700, color: 'warning.main', fontSize: 13 }}>{r.code}</Typography>
                    <Typography sx={{ fontSize: 13 }}>
                      {r.suggested_linga || '—'}
                      {fmtAmt(r.price) && <Box component="span" sx={{ color: 'success.main', fontWeight: 700 }}>{` · ${fmtAmt(r.price)}`}</Box>}
                    </Typography>
                    {showSponsors && <Typography sx={{ fontSize: 13, color: r.sponsor ? 'text.primary' : 'text.secondary' }}>{r.sponsor || 'Available'}</Typography>}
                  </React.Fragment>
                ))}
              </Box>
            </DialogContent>
          </Dialog>
        );
      })()}
      <Snackbar open={Boolean(toast)} autoHideDuration={1500} onClose={() => setToast('')} message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </PageLayout>
  );
}
